import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { IRootState } from '@/store';
import { markRead as markReadAction, markAllRead, clearAll } from '@/store/notificationsSlice';
import type { Notification } from '@/store/notificationsSlice';
import Dropdown, { type DropdownHandle } from '@/components/Dropdown';
import { formatIsoDatesInTextBR } from '@/lib/date';
import NotificationsDrawer from '@/components/Notifications/NotificationsDrawer';

/** Returns a human-readable relative time string without date-fns */
function relativeTime(isoString: string): string {
    const diff = Date.now() - new Date(isoString).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return 'agora mesmo';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}min atrás`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h atrás`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d atrás`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} mes${months > 1 ? 'es' : ''} atrás`;
    const years = Math.floor(months / 12);
    return `${years} ano${years > 1 ? 's' : ''} atrás`;
}

const BellIcon = ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z" fill="currentColor" />
        <path
            opacity="0.5"
            d="M21 19L20 18V11C20 7.2 17.4 4.1 14 3.2V2C14 0.9 13.1 0 12 0C10.9 0 10 0.9 10 2V3.2C6.6 4.1 4 7.2 4 11V18L3 19C2.4 19.6 2.8 20.7 3.7 20.7H20.4C21.2 20.7 21.6 19.6 21 19Z"
            fill="currentColor"
        />
    </svg>
);

export const NotificationsBell = ({ size }: { size?: 'sm' }) => {
    const dropdownRef = useRef<DropdownHandle>(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { list, unreadCount } = useSelector((s: IRootState) => s.notifications);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const recent = list.slice(0, 10);
    const compact = size === 'sm';

    const handleOpenDrawer = () => {
        dropdownRef.current?.close();
        setDrawerOpen(true);
    };

    const handleClickNotification = async (n: Notification) => {
        if (!n.readAt) {
            try {
                await api.patch(`/api/notifications/${n.id}/read`);
                dispatch(markReadAction(n.id));
            } catch {
                /* noop */
            }
        }
        dropdownRef.current?.close();
        if (n.url && n.url !== '/') {
            navigate(n.url);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.patch('/api/notifications/read-all');
            dispatch(markAllRead());
        } catch {
            /* noop */
        }
        dropdownRef.current?.close();
    };

    const handleClearAll = async () => {
        try {
            await api.delete('/api/notifications');
            dispatch(clearAll());
        } catch {
            /* noop */
        }
        dropdownRef.current?.close();
    };

    return (
        <div className="relative shrink-0">
            <Dropdown
                ref={dropdownRef}
                offset={[0, 8]}
                placement="bottom-end"
                btnClassName={`relative flex items-center justify-center rounded-full bg-white-light/40 dark:bg-surface-hover hover:text-primary hover:bg-white-light/90 dark:hover:bg-surface-border ${compact ? 'p-1.5' : 'p-2.5'}`}
                button={
                    <>
                        <BellIcon size={compact ? 18 : 20} />
                        {unreadCount > 0 && (
                            <span className="absolute top-0.5 ltr:right-0.5 rtl:left-0.5 flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white rounded-full bg-danger">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </>
                }
            >
                <div className="w-[320px] sm:w-[360px] max-h-[480px] flex flex-col rounded-lg shadow-lg bg-white dark:bg-surface-card border border-white-light dark:border-surface-border overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white-light dark:border-surface-border">
                        <span className="font-semibold text-sm text-dark dark:text-white-light">
                            Notificações
                            {unreadCount > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-danger text-white">{unreadCount}</span>
                            )}
                        </span>
                        <div className="flex items-center gap-3">
                            {unreadCount > 0 && (
                                <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline font-semibold">
                                    Marcar como lidas
                                </button>
                            )}
                            {recent.length > 0 && (
                                <button onClick={handleClearAll} className="text-xs text-danger hover:underline font-semibold">
                                    Limpar
                                </button>
                            )}
                        </div>
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto flex-1">
                        {recent.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-white-dark/40 gap-2">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z" fill="currentColor" />
                                    <path
                                        opacity="0.3"
                                        d="M21 19L20 18V11C20 7.2 17.4 4.1 14 3.2V2C14 0.9 13.1 0 12 0C10.9 0 10 0.9 10 2V3.2C6.6 4.1 4 7.2 4 11V18L3 19C2.4 19.6 2.8 20.7 3.7 20.7H20.4C21.2 20.7 21.6 19.6 21 19Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <p className="text-sm">Nenhuma notificação</p>
                            </div>
                        ) : (
                            recent.map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => handleClickNotification(n)}
                                    className={`w-full text-left px-4 py-3 border-b border-white-light dark:border-surface-border last:border-b-0 hover:bg-gray-50 dark:hover:bg-surface-hover transition-colors ${!n.readAt ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${!n.readAt ? 'bg-primary' : 'bg-transparent'}`} />
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-semibold truncate ${!n.readAt ? 'text-dark dark:text-white' : 'text-gray-600 dark:text-white-dark'}`}>{n.title}</p>
                                            {n.body && <p className="text-xs text-gray-500 dark:text-white-dark/60 mt-0.5 line-clamp-2">{formatIsoDatesInTextBR(n.body)}</p>}
                                            <p className="text-[10px] text-gray-400 dark:text-white-dark/40 mt-1">{relativeTime(n.createdAt)}</p>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2.5 border-t border-white-light dark:border-surface-border text-center">
                        <button type="button" onClick={handleOpenDrawer} className="text-xs font-semibold text-primary hover:underline">
                            Ver todas as notificações
                        </button>
                    </div>
                </div>
            </Dropdown>

            <NotificationsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        </div>
    );
};

export default NotificationsBell;
